const PROCESSING_LIST_KEYS = new Set([
  'id',
  'campaignId',
  'mailingId',
  'intent',
  'prospectId',
  'processingState',
  'submittedAt',
  'address',
  'city',
  'ownerName',
  'prospectStatus',
  'processedAt',
  'updatedAt',
  'updatedAtVersion',
]);

const PROCESSING_LIST_FORBIDDEN = new Set([
  'email',
  'phone',
  'grossAnnual',
  'notes',
  'primaryContact',
]);

const PROSPECT_LIST_KEYS = new Set([
  'id',
  'address',
  'city',
  'province',
  'postalCode',
  'numberOfUnits',
  'ownerName',
  'status',
  'source',
  'lastContactAt',
  'nextFollowUpAt',
  'createdAt',
  'updatedAt',
  'updatedAtVersion',
  'followUpBucket',
]);

const PROSPECT_LIST_FORBIDDEN = new Set([
  'email',
  'phone',
  'notes',
  'primaryContact',
  'ownerMailingAddress',
  'grossAnnualIncomeCents',
]);

function optionalString(value) {
  return typeof value === 'string' && value.trim() ? value : null;
}

function optionalIso(value) {
  if (value == null) return null;
  return typeof value === 'string' ? value : null;
}

function parseUpdatedAtVersion(raw) {
  if (
    raw &&
    typeof raw === 'object' &&
    !Array.isArray(raw) &&
    Number.isInteger(raw.s) &&
    Number.isInteger(raw.n) &&
    raw.n >= 0 &&
    raw.n < 1e9
  ) {
    return { s: raw.s, n: raw.n };
  }
  return null;
}

/** Key-only deny list — display string values may contain forbidden substrings. */
function assertNoForbiddenKeys(obj, forbidden, label) {
  for (const key of forbidden) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      throw new TypeError(`${label} leaked forbidden field: ${key}`);
    }
  }
}

function parseProcessingItem(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new TypeError('Invalid processing list item');
  }
  for (const key of Object.keys(raw)) {
    if (!PROCESSING_LIST_KEYS.has(key)) {
      throw new TypeError(`Processing list item leaked field: ${key}`);
    }
  }
  assertNoForbiddenKeys(raw, PROCESSING_LIST_FORBIDDEN, 'Processing list item');
  return {
    id: optionalString(raw.id),
    campaignId: optionalString(raw.campaignId),
    mailingId: optionalString(raw.mailingId),
    intent: optionalString(raw.intent),
    prospectId: optionalString(raw.prospectId),
    processingState:
      raw.processingState === 'processed' ? 'processed' : 'needs_processing',
    submittedAt: optionalIso(raw.submittedAt),
    address: optionalString(raw.address),
    city: optionalString(raw.city),
    ownerName: optionalString(raw.ownerName),
    prospectStatus: optionalString(raw.prospectStatus),
    processedAt: optionalIso(raw.processedAt),
    updatedAt: optionalIso(raw.updatedAt),
    updatedAtVersion: parseUpdatedAtVersion(raw.updatedAtVersion),
  };
}

function parseProspectListItem(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new TypeError('Invalid prospect list item');
  }
  for (const key of Object.keys(raw)) {
    if (!PROSPECT_LIST_KEYS.has(key)) {
      throw new TypeError(`Prospect list item leaked field: ${key}`);
    }
  }
  assertNoForbiddenKeys(raw, PROSPECT_LIST_FORBIDDEN, 'Prospect list item');
  const item = {
    id: optionalString(raw.id),
    address: optionalString(raw.address),
    city: optionalString(raw.city),
    province: optionalString(raw.province),
    postalCode: optionalString(raw.postalCode),
    numberOfUnits: Number.isInteger(raw.numberOfUnits) ? raw.numberOfUnits : null,
    ownerName: optionalString(raw.ownerName),
    status: optionalString(raw.status),
    source: optionalString(raw.source),
    lastContactAt: optionalIso(raw.lastContactAt),
    nextFollowUpAt: optionalIso(raw.nextFollowUpAt),
    createdAt: optionalIso(raw.createdAt),
    updatedAt: optionalIso(raw.updatedAt),
    updatedAtVersion: parseUpdatedAtVersion(raw.updatedAtVersion),
  };
  if (optionalString(raw.followUpBucket)) item.followUpBucket = raw.followUpBucket;
  return item;
}

export function parseListAdminProspectResponsesToProcess(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new TypeError('Invalid responses-to-process DTO');
  }
  const items = Array.isArray(data.items) ? data.items.map(parseProcessingItem) : [];
  const coverage =
    data.coverage === 'complete' || data.coverage === 'incomplete' || data.coverage === 'unknown'
      ? data.coverage
      : 'unknown';
  return {
    items,
    pageSize: Number.isInteger(data.pageSize) ? data.pageSize : items.length,
    processingState:
      data.processingState === 'processed' ? 'processed' : 'needs_processing',
    nextPageToken: typeof data.nextPageToken === 'string' ? data.nextPageToken : null,
    coverage,
  };
}

export function parseSetAdminProspectPublicResponseProcessing(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new TypeError('Invalid processing set DTO');
  }
  return { item: parseProcessingItem(data.item) };
}

export function parseListAdminProspectFollowUps(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new TypeError('Invalid follow-ups DTO');
  }
  const items = Array.isArray(data.items) ? data.items.map(parseProspectListItem) : [];
  return {
    items,
    bucket: typeof data.bucket === 'string' ? data.bucket : 'all',
    timeZone: typeof data.timeZone === 'string' ? data.timeZone : null,
    dayStart: optionalIso(data.dayStart),
    pageSize: Number.isInteger(data.pageSize) ? data.pageSize : items.length,
    nextPageToken: typeof data.nextPageToken === 'string' ? data.nextPageToken : null,
  };
}

export function parseListAdminProspectActiveOpportunities(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new TypeError('Invalid opportunities DTO');
  }
  const items = Array.isArray(data.items) ? data.items.map(parseProspectListItem) : [];
  return {
    items,
    pageSize: Number.isInteger(data.pageSize) ? data.pageSize : items.length,
    status: typeof data.status === 'string' ? data.status : 'all',
    nextPageToken: typeof data.nextPageToken === 'string' ? data.nextPageToken : null,
  };
}
